import type { DebouncedFunc } from 'lodash';
import debounce from 'lodash/debounce';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import { type Command, type ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { ViewMode } from '@atlaskit/editor-plugin-editor-viewmode';
import { DecorationSet, type EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIDefinitionsPlugin } from '../../aiDefinitionsPluginType';
import type {
	AIDefinitionsPluginConfig,
	OpenReadingAidsPopupSource,
	ReadingAidsPopupPropsInState,
} from '../../types';
import {
	getAIDefinitionsErrorAnalyticsPayload,
	getOpenReadingAidsPopupAnalyticsPayload,
} from '../../ui/utils/analytics';

import { type AIDefinitionsAction, AIDefinitionsActionType } from './actions';
import { createCommand, getPluginState } from './plugin-factory';
import { createKeywordDecorationsForDoc } from './util/keyword';
import {
	resetKeywordStylerLoading,
	setupKeywordStylerMutationObserver,
	updateAllKeywordStylerPositions,
} from './util/keyword-styler';

export const updateKeywordRegex = ({ keywordRegex }: { keywordRegex: RegExp }): Command => {
	return createCommand<AIDefinitionsAction>({
		type: AIDefinitionsActionType.UPDATE_KEYWORD_REGEX,
		keywordRegex,
	});
};

export const updateKeywordDecorationsSessionId = (): Command => {
	return createCommand<AIDefinitionsAction>({
		type: AIDefinitionsActionType.UPDATE_KEYWORD_DECORATIONS_SESSION_ID,
	});
};

export const openReadingAidsPopup = ({
	api,
	source,
	keywordId,
	readingAidsPopupProps,
}: {
	api: ExtractInjectionAPI<AIDefinitionsPlugin> | undefined;
	keywordId?: string;
	readingAidsPopupProps: ReadingAidsPopupPropsInState;
	source: OpenReadingAidsPopupSource;
}): Command => {
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	const readingAidsSessionId = uuid();

	return createCommand<AIDefinitionsAction>(
		{
			type: AIDefinitionsActionType.OPEN_READING_AIDS_POPUP,
			keywordId,
			readingAidsPopupProps: { ...readingAidsPopupProps, sessionId: readingAidsSessionId },
		},
		(tr) => {
			if (api) {
				// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
				const wordCount = readingAidsPopupProps.selectedText.split(/\s+/u).length;
				const payload = getOpenReadingAidsPopupAnalyticsPayload({
					source,
					readingAidsSessionId,
					wordCount,
				});
				api.analytics?.actions.attachAnalyticsEvent(payload)(tr);
			}
			return tr;
		},
	);
};

export const closeReadingAidsPopup = (): Command => {
	return createCommand<AIDefinitionsAction>({
		type: AIDefinitionsActionType.CLOSE_READING_AIDS_POPUP,
	});
};

export const readingAidsPopupLoaded = (): Command => {
	return createCommand<AIDefinitionsAction>({
		type: AIDefinitionsActionType.READING_AIDS_POPUP_LOADED,
	});
};

export const updateKeywordDecorations = ({
	keywordDecorations,
}: {
	keywordDecorations: DecorationSet;
}): Command => {
	return createCommand<AIDefinitionsAction>({
		type: AIDefinitionsActionType.UPDATE_KEYWORD_DECORATIONS,
		keywordDecorations,
	});
};

export const removeKeywordDecorations = (): Command => {
	return updateKeywordDecorations({ keywordDecorations: DecorationSet.empty });
};

function isKeywordDecorationsSessionIdChanged({
	editorView,
	keywordDecorationsSessionId,
}: {
	editorView: EditorView;
	keywordDecorationsSessionId: string;
}) {
	const aiDefinitionsPluginState = getPluginState(editorView.state);
	return keywordDecorationsSessionId !== aiDefinitionsPluginState.keywordDecorationsSessionId;
}

export const updateKeywordsAndDecorations = async ({
	api,
	config,
	editorView,
}: {
	api: ExtractInjectionAPI<AIDefinitionsPlugin> | undefined;
	config: AIDefinitionsPluginConfig;
	editorView: EditorView;
}): Promise<void> => {
	try {
		const aiDefinitionsPluginState = getPluginState(editorView.state);
		let { keywordRegex } = aiDefinitionsPluginState;

		// if keywordRegex is not available, fetch it and update decorations
		if (!keywordRegex) {
			const { keywordDecorationsSessionId } = aiDefinitionsPluginState;
			const res = await config.queryHighlightAcronyms?.({ cacheKey: keywordDecorationsSessionId });
			// if sessionId changed, abandon the response
			if (isKeywordDecorationsSessionIdChanged({ editorView, keywordDecorationsSessionId })) {
				return;
			}

			if (res && res.acronymRegex) {
				keywordRegex = res.acronymRegex;
				updateKeywordRegex({ keywordRegex })(editorView.state, editorView.dispatch);
				const keywordDecorations = createKeywordDecorationsForDoc({ editorView, keywordRegex });
				updateKeywordDecorations({ keywordDecorations })(editorView.state, editorView.dispatch);
			}
		}
	} catch (error) {
		api?.analytics?.actions.fireAnalyticsEvent(getAIDefinitionsErrorAnalyticsPayload({ error }));
	}
};

export const debouncedUpdateKeywordsAndDecorations: DebouncedFunc<
	({
		api,
		config,
		editorView,
	}: {
		api: ExtractInjectionAPI<AIDefinitionsPlugin> | undefined;
		config: AIDefinitionsPluginConfig;
		editorView: EditorView;
	}) => Promise<void>
> = debounce(updateKeywordsAndDecorations, 1000);

/**
 *
 * @param root0
 * @param root0.editorView
 * @example
 */
export function handleCloseReadingAidsPopup({ editorView }: { editorView: EditorView }): void {
	resetKeywordStylerLoading();
	closeReadingAidsPopup()(editorView.state, editorView.dispatch);
}

export const onLivePageModeChange = ({
	api,
	config,
	editorView,
	autoHighlightEnabled,
	livePageMode,
}: {
	api: ExtractInjectionAPI<AIDefinitionsPlugin> | undefined;
	autoHighlightEnabled?: boolean;
	config: AIDefinitionsPluginConfig;
	editorView: EditorView;
	livePageMode: ViewMode | undefined;
}): (() => void) | undefined => {
	// we want to closeReadingAidsPopup if there is one opened
	handleCloseReadingAidsPopup({ editorView });

	// currently, only show auto-highlights in view mode
	if (autoHighlightEnabled && livePageMode === 'view') {
		const cleanObserver = setupKeywordStylerMutationObserver(editorView.dom);

		// when the decorations aren't changed, the DOM nodes are already inserted when `view` is called
		// - only happens on Hello (not locally)
		// so there must be time racing somehow
		// so update DOM nodes positions here to make sure they are all positioned correctly
		updateAllKeywordStylerPositions();

		updateKeywordsAndDecorations({ api, config, editorView });

		return cleanObserver;
	}
};
